import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getRoutines, createRoutine, updateRoutine, deleteRoutine, getRoutineLogs, logRoutine, extractErrorMessage } from '../services/api';
import { FaPlus, FaTrash, FaEdit, FaClipboardCheck } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

function Routines({ user }) {
  const toast = useToast();
  const [routines, setRoutines] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRoutineModal, setShowRoutineModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [editingRoutine, setEditingRoutine] = useState(null);
  const [activeTab, setActiveTab] = useState('routines');
  const [savingRoutine, setSavingRoutine] = useState(false);
  const [formErrorRoutine, setFormErrorRoutine] = useState(null);
  const [savingLog, setSavingLog] = useState(false);
  const [formErrorLog, setFormErrorLog] = useState(null);
  const [newRoutine, setNewRoutine] = useState({
    user_id: '',
    type: 'morning',
    name: '',
    description: '',
    schedule: '',
    instructions: ''
  });
  const [newLog, setNewLog] = useState({
    routine_id: '',
    notes: '',
    quality: 5
  });

  useEffect(() => {
    fetchRoutines();
    fetchLogs();
  }, []);

  const fetchRoutines = async () => {
    try {
      setLoading(true);
      const data = await getRoutines();
      setRoutines(data.routines || data || []);
    } catch (error) {
      console.error('Error fetching routines:', error);
      toast.error(extractErrorMessage(error, 'Routinen konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const data = await getRoutineLogs();
      setLogs(data.logs || data || []);
    } catch (error) {
      console.error('Error fetching routine logs:', error);
      toast.error(extractErrorMessage(error, 'Routine-Logs konnten nicht geladen werden'));
    }
  };

  const handleCreateRoutine = async (e) => {
    e.preventDefault();
    if (savingRoutine) return;
    setSavingRoutine(true);
    setFormErrorRoutine(null);
    try {
      if (editingRoutine) {
        await updateRoutine(editingRoutine.id, newRoutine);
      } else {
        await createRoutine(newRoutine);
      }
      setEditingRoutine(null);
      setNewRoutine({ user_id: '', type: 'morning', name: '', description: '', schedule: '', instructions: '' });
      setShowRoutineModal(false);
      toast.success(editingRoutine ? 'Routine aktualisiert' : 'Routine erstellt');
      fetchRoutines();
    } catch (error) {
      console.error('Error saving routine:', error);
      setFormErrorRoutine(extractErrorMessage(error, 'Fehler beim Speichern der Routine'));
    } finally {
      setSavingRoutine(false);
    }
  };

  const handleLogRoutine = async (e) => {
    e.preventDefault();
    if (savingLog) return;
    setSavingLog(true);
    setFormErrorLog(null);
    try {
      await logRoutine(newLog.routine_id, newLog);
      setNewLog({ routine_id: '', notes: '', quality: 5 });
      setShowLogModal(false);
      toast.success('Routine-Eintrag gespeichert');
      fetchLogs();
    } catch (error) {
      console.error('Error logging routine:', error);
      setFormErrorLog(extractErrorMessage(error, 'Fehler beim Loggen der Routine'));
    } finally {
      setSavingLog(false);
    }
  };

  const closeRoutineModal = () => {
    if (savingRoutine) return;
    setShowRoutineModal(false);
    setFormErrorRoutine(null);
  };

  const closeLogModal = () => {
    if (savingLog) return;
    setShowLogModal(false);
    setFormErrorLog(null);
  };

  const handleDeleteRoutine = async (id) => {
    if (!window.confirm('Routine wirklich löschen?')) return;
    try {
      await deleteRoutine(id);
      fetchRoutines();
    } catch (error) {
      console.error('Error deleting routine:', error);
    }
  };

  const handleEditRoutine = (routine) => {
    setEditingRoutine(routine);
    setNewRoutine({
      user_id: routine.user_id || '',
      type: routine.type,
      name: routine.name,
      description: routine.description || '',
      schedule: routine.schedule || '',
      instructions: routine.instructions || ''
    });
    setShowRoutineModal(true);
  };

  const routineTypes = ['morning', 'evening', 'custom'];

  const getTypeLabel = (type) => {
    const labels = { morning: 'Morgens', evening: 'Abends', custom: 'Individuell' };
    return labels[type] || type;
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Routinen</h1>
          <div className="header-actions">
            <button className="add-btn" onClick={() => { setEditingRoutine(null); setNewRoutine({ user_id: '', type: 'morning', name: '', description: '', schedule: '', instructions: '' }); setShowRoutineModal(true); }}>
              <FaPlus /> Neue Routine
            </button>
            <button className="add-btn secondary" onClick={() => setShowLogModal(true)}>
              <FaClipboardCheck /> Routine loggen
            </button>
          </div>
        </div>

        <div className="tab-bar">
          <button className={`tab ${activeTab === 'routines' ? 'active' : ''}`} onClick={() => setActiveTab('routines')}>Routinen</button>
          <button className={`tab ${activeTab === 'logs' ? 'active' : ''}`} onClick={() => setActiveTab('logs')}>Protokoll</button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'routines' ? (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Typ</th>
                  <th>Beschreibung</th>
                  <th>Zeitplan</th>
                  <th>Anweisungen</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {routines.length > 0 ? routines.map(routine => (
                  <tr key={routine.id}>
                    <td><strong>{routine.name}</strong></td>
                    <td><span className="badge">{getTypeLabel(routine.type)}</span></td>
                    <td>{routine.description || '-'}</td>
                    <td>{routine.schedule || '-'}</td>
                    <td>{routine.instructions ? routine.instructions.substring(0, 80) + (routine.instructions.length > 80 ? '...' : '') : '-'}</td>
                    <td className="actions-cell">
                      <button className="icon-btn edit" onClick={() => handleEditRoutine(routine)} title="Bearbeiten"><FaEdit /></button>
                      <button className="icon-btn delete" onClick={() => handleDeleteRoutine(routine.id)} title="Löschen"><FaTrash /></button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="6" className="no-data">Keine Routinen gefunden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Routine</th>
                  <th>Qualität</th>
                  <th>Notizen</th>
                </tr>
              </thead>
              <tbody>
                {logs.length > 0 ? logs.map(log => (
                  <tr key={log.id}>
                    <td>{new Date(log.created_at || log.timestamp).toLocaleDateString('de-DE')}</td>
                    <td>{log.routine_name || log.routine_id}</td>
                    <td>
                      <div className="meter-bar inline">
                        <div className="meter-fill" style={{ width: `${(log.quality / 10) * 100}%` }}></div>
                      </div>
                      <span>{log.quality}/10</span>
                    </td>
                    <td>{log.notes || '-'}</td>
                  </tr>
                )) : (
                  <tr><td colSpan="4" className="no-data">Keine Einträge gefunden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showRoutineModal && (
          <div className="modal-overlay" onClick={closeRoutineModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>{editingRoutine ? 'Routine bearbeiten' : 'Neue Routine'}</h2>
              {formErrorRoutine && <div className="shared-form-error-banner">{formErrorRoutine}</div>}
              <form onSubmit={handleCreateRoutine}>
                <div className="form-group">
                  <UserSelect
                    value={newRoutine.user_id}
                    onChange={id => setNewRoutine({...newRoutine, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Typ</label>
                  <select
                    value={newRoutine.type}
                    onChange={e => setNewRoutine({...newRoutine, type: e.target.value})}
                  >
                    {routineTypes.map(t => (
                      <option key={t} value={t}>{getTypeLabel(t)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={newRoutine.name}
                    onChange={e => setNewRoutine({...newRoutine, name: e.target.value})}
                    placeholder="Routine-Name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={newRoutine.description}
                    onChange={e => setNewRoutine({...newRoutine, description: e.target.value})}
                    placeholder="Beschreibung..."
                  />
                </div>
                <div className="form-group">
                  <label>Zeitplan</label>
                  <input
                    type="text"
                    value={newRoutine.schedule}
                    onChange={e => setNewRoutine({...newRoutine, schedule: e.target.value})}
                    placeholder="z.B. Täglich um 7:00 Uhr"
                  />
                </div>
                <div className="form-group">
                  <label>Anweisungen</label>
                  <textarea
                    value={newRoutine.instructions}
                    onChange={e => setNewRoutine({...newRoutine, instructions: e.target.value})}
                    placeholder="Schritt-für-Schritt Anweisungen..."
                    rows="4"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeRoutineModal} disabled={savingRoutine}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingRoutine}>{savingRoutine ? 'Speichert…' : (editingRoutine ? 'Speichern' : 'Erstellen')}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showLogModal && (
          <div className="modal-overlay" onClick={closeLogModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Routine loggen</h2>
              {formErrorLog && <div className="shared-form-error-banner">{formErrorLog}</div>}
              <form onSubmit={handleLogRoutine}>
                <div className="form-group">
                  <label>Routine *</label>
                  <select
                    value={newLog.routine_id}
                    onChange={e => setNewLog({...newLog, routine_id: e.target.value})}
                    required
                  >
                    <option value="">Routine auswählen...</option>
                    {routines.map(r => (
                      <option key={r.id} value={r.id}>{r.name} ({getTypeLabel(r.type)})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Qualität: {newLog.quality}/10</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newLog.quality}
                    onChange={e => setNewLog({...newLog, quality: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newLog.notes}
                    onChange={e => setNewLog({...newLog, notes: e.target.value})}
                    placeholder="Wie lief die Routine?"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeLogModal} disabled={savingLog}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingLog}>{savingLog ? 'Speichert…' : 'Loggen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Routines;
